import { useReducer, useCallback, useMemo } from "react";
import type { ReportProjectionType } from "../../../global/constants/projectionLayers";
import type { EnergyType, ReportScope } from "../model/types";

export interface ProgressState {
  message: string;
  percentage: number;
}

export interface ReportState {
  projectName: string;
  projectDescription: string;
  scope: ReportScope;
  energies: EnergyType[];
  /** Nível detalhado nos memoriais — independente do que está ativo em Dados Técnicos. */
  level: ReportProjectionType;
  includeContextLayers: boolean;
  /** IDs das Marcações escolhidas, na ordem em que foram marcadas. */
  selectedMarkings: string[];
  progress: ProgressState;
  isGenerating: boolean;
  error: string | null;
}

export type ReportAction =
  | { type: "SET_PROJECT_NAME"; payload: string }
  | { type: "SET_PROJECT_DESCRIPTION"; payload: string }
  | { type: "SET_SCOPE"; payload: ReportScope }
  | { type: "TOGGLE_ENERGY"; payload: EnergyType }
  | { type: "SET_LEVEL"; payload: ReportProjectionType }
  | { type: "TOGGLE_CONTEXT_LAYERS" }
  | { type: "TOGGLE_MARKING"; payload: string }
  | { type: "SET_PROGRESS"; payload: ProgressState }
  | { type: "START_GENERATION" }
  | { type: "STOP_GENERATION" }
  | { type: "SET_ERROR"; payload: string | null };

const IDLE_PROGRESS: ProgressState = { message: "", percentage: 0 };

export const initialReportState: ReportState = {
  projectName: "",
  projectDescription: "",
  scope: "marcacao",
  energies: ["solar", "eolico"],
  level: "teorico",
  includeContextLayers: true,
  selectedMarkings: [],
  progress: IDLE_PROGRESS,
  isGenerating: false,
  error: null,
};

/** Quantas Marcações cada escopo aceita. */
export const MARKINGS_PER_SCOPE: Record<ReportScope, number> = {
  marcacao: 1,
  comparativo: 2,
  estado: 0,
};

const ENERGY_ORDER: EnergyType[] = ["solar", "eolico"];

export function reportReducer(state: ReportState, action: ReportAction): ReportState {
  switch (action.type) {
    case "SET_PROJECT_NAME":
      return { ...state, projectName: action.payload };

    case "SET_PROJECT_DESCRIPTION":
      return { ...state, projectDescription: action.payload };

    case "SET_SCOPE": {
      // Ao reduzir o limite, ficam as Marcações mais antigas — a primeira
      // escolhida continua sendo a "área A".
      const limit = MARKINGS_PER_SCOPE[action.payload];
      return {
        ...state,
        scope: action.payload,
        selectedMarkings: limit === 0 ? state.selectedMarkings : state.selectedMarkings.slice(0, limit),
      };
    }

    case "TOGGLE_ENERGY": {
      const has = state.energies.includes(action.payload);
      // Sempre sobra ao menos um tipo de energia.
      if (has && state.energies.length === 1) return state;
      const next = has
        ? state.energies.filter((e) => e !== action.payload)
        : [...state.energies, action.payload];
      return { ...state, energies: ENERGY_ORDER.filter((e) => next.includes(e)) };
    }

    case "SET_LEVEL":
      return { ...state, level: action.payload };

    case "TOGGLE_CONTEXT_LAYERS":
      return { ...state, includeContextLayers: !state.includeContextLayers };

    case "TOGGLE_MARKING": {
      const id = action.payload;
      if (state.selectedMarkings.includes(id)) {
        return { ...state, selectedMarkings: state.selectedMarkings.filter((m) => m !== id) };
      }
      const limit = Math.max(1, MARKINGS_PER_SCOPE[state.scope]);
      // Passou do limite: sai a mais antiga, como num seletor de 1 ou 2 vagas.
      const next = [...state.selectedMarkings, id].slice(-limit);
      return { ...state, selectedMarkings: next };
    }

    case "SET_PROGRESS":
      return { ...state, progress: action.payload };

    case "START_GENERATION":
      return { ...state, isGenerating: true, error: null, progress: IDLE_PROGRESS };

    case "STOP_GENERATION":
      return { ...state, isGenerating: false, progress: IDLE_PROGRESS };

    case "SET_ERROR":
      return { ...state, isGenerating: false, error: action.payload, progress: IDLE_PROGRESS };

    default:
      return state;
  }
}

export function useReportState(initialDrawingId?: string | null) {
  const [state, dispatch] = useReducer(reportReducer, {
    ...initialReportState,
    selectedMarkings: initialDrawingId ? [initialDrawingId] : [],
  });

  const setProjectName = useCallback(
    (payload: string) => dispatch({ type: "SET_PROJECT_NAME", payload }),
    []
  );
  const setProjectDescription = useCallback(
    (payload: string) => dispatch({ type: "SET_PROJECT_DESCRIPTION", payload }),
    []
  );
  const setScope = useCallback(
    (payload: ReportScope) => dispatch({ type: "SET_SCOPE", payload }),
    []
  );
  const toggleEnergy = useCallback(
    (payload: EnergyType) => dispatch({ type: "TOGGLE_ENERGY", payload }),
    []
  );
  const setLevel = useCallback(
    (payload: ReportProjectionType) => dispatch({ type: "SET_LEVEL", payload }),
    []
  );
  const toggleContextLayers = useCallback(
    () => dispatch({ type: "TOGGLE_CONTEXT_LAYERS" }),
    []
  );
  const toggleMarking = useCallback(
    (payload: string) => dispatch({ type: "TOGGLE_MARKING", payload }),
    []
  );
  const setProgress = useCallback(
    (payload: ProgressState) => dispatch({ type: "SET_PROGRESS", payload }),
    []
  );
  const startGeneration = useCallback(() => dispatch({ type: "START_GENERATION" }), []);
  const stopGeneration = useCallback(() => dispatch({ type: "STOP_GENERATION" }), []);
  const setError = useCallback(
    (payload: string | null) => dispatch({ type: "SET_ERROR", payload }),
    []
  );

  const actions = useMemo(
    () => ({
      setProjectName,
      setProjectDescription,
      setScope,
      toggleEnergy,
      setLevel,
      toggleContextLayers,
      toggleMarking,
      setProgress,
      startGeneration,
      stopGeneration,
      setError,
    }),
    [
      setProjectName,
      setProjectDescription,
      setScope,
      toggleEnergy,
      setLevel,
      toggleContextLayers,
      toggleMarking,
      setProgress,
      startGeneration,
      stopGeneration,
      setError,
    ]
  );

  return { state, actions };
}
